"use client";

import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type { ManualCategory, ManualTopic, ManualUpdatesResponse } from "@/types/manual";

export function useManualCategories() {
  const [data, setData] = useState<ManualCategory[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/v1/manual/categories");
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const result = await res.json();
      setData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch categories"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useManualTopic(id: number | null) {
  const [data, setData] = useState<ManualTopic | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (id === null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/manual/topics/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const result = await res.json();
      setData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch topic"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useManualAllTopics() {
  const [data, setData] = useState<ManualTopic[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/v1/manual/topics");
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const result = await res.json();
      setData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch topics"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useManualUpdates() {
  const [data, setData] = useState<ManualUpdatesResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/v1/manual/updates");
      if (!res.ok) return;
      const result = await res.json();
      setData(result.data);
    } catch {
      // Silently fail for notification badge
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, refetch: fetch };
}
